import React from 'react';
import { commitLocalUpdate, useRelayEnvironment } from 'react-relay';
import {
  MobilePlanEligibilityCheck,
  useGlobalUser,
  useLoginModal,
  useLogout,
} from '@sparknz/journeys-common';
import { updateTrackedUser } from '@sparknz/tracking';

import { definitions } from '__generated__/openapi-specs/ProductEligibilityService';
import {
  usePlansRefetch,
  useSelectedPlans,
} from 'components/shop/MobilePlans/providers';
import { SelectedPlan } from 'components/shop/MobilePlans/typings/plans';
import { MOBILE_PLANS_PATH } from 'shared/constants/path.constants';
import { usePageConfig } from 'shared/providers/PageConfigProvider';
import { PlanConfigurationStep } from '../PlanConfiguration/PlanConfiguration';

interface EligibilityChecksProps {
  selectedPlan: SelectedPlan;
  navigate(to: PlanConfigurationStep): void;
  setSelectedAccount(eligibleAccount?: definitions['EligibleAccount']): void;
}

const EligibilityChecks = ({
  selectedPlan,
  navigate,
  setSelectedAccount,
}: EligibilityChecksProps) => {
  const { offerId } = selectedPlan;
  const { apiBaseUrl, cartCookieName } = usePageConfig();
  const { requestLogout } = useLogout();
  const environment = useRelayEnvironment();
  const { refetchUser } = useGlobalUser();
  const { fetchKey, setFetchKey } = usePlansRefetch();
  const { closeSelectedPlansLayer } = useSelectedPlans();
  const { openLoginModal } = useLoginModal();

  const handleSuccess = (eligibleAccount: definitions['EligibleAccount']) => {
    setSelectedAccount(eligibleAccount);
    navigate('SELECT_QUANTITY');
  };

  const handleLogout = async () => {
    const { success } = await requestLogout();
    if (success) {
      window.location.assign(`${MOBILE_PLANS_PATH}?plan=endless_plans`);
    }
  };

  const handleLoginSuccess = () => {
    updateTrackedUser();
    window.location.assign(`${MOBILE_PLANS_PATH}?plan=kids_plan`);
  };

  const handleTryAnotherLogin = async () => {
    const { success } = await requestLogout();
    if (success) {
      commitLocalUpdate(environment, (store) => {
        store.invalidateStore();
      });
      setFetchKey(fetchKey + 1);
      await refetchUser();
      openLoginModal({ onLoginSuccess: handleLoginSuccess });
      closeSelectedPlansLayer();
    }
  };

  return (
    <MobilePlanEligibilityCheck
      apiBaseUrl={apiBaseUrl}
      cartCookieName={cartCookieName}
      selectedPlanOfferId={offerId}
      onSuccess={handleSuccess}
      onContinueAsNewCustomer={handleLogout}
      onTryAnotherLogin={handleTryAnotherLogin}
    />
  );
};

export default EligibilityChecks;
